import { Box, useTheme } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { epochToString } from "../../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { routes } from "../../../../../app/paths";
import { GroupedTable } from "../../../../../common/components/functional/GroupedTable/GroupedTable";
import useGroupedTableSearch from "../../../../../common/components/functional/GroupedTable/search/useGroupedTableSearch";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { UnderlineLink } from "../../../../../common/components/styled/styled";
import { getName } from "../../../../../common/helpers/common";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import {
  ItemGroup,
  ItemGroupLoadingState,
} from "../../../../invoices/invoiceAPI";
import { JobAdApplicant } from "../../../../jobAds/jodAdApplicants/jobAdApplicantsAPI";
import { AssignmentCoverLetter } from "../../../../talentProfile/assignmentCoverLetter/AssignmentCoverLetter";
import { TalentExclusivity } from "../../../../talents/talentAPI";
import WorkAgreementBlockingDialog from "../../../dialogs/WorkAgreementBlockingDialog";
import { AssignedTalent, OrderEdit } from "../../../orderAPI";
import { Candidate, PickedCandidate } from "../../../TalentsStatusAPI";
import {
  fetchCandidates,
  isLongOrderNewTalentStatus,
  selectAllPickedCandidates,
  selectAllRecommendedCandidates,
  selectAllTalentStatusJobAdApplicants,
} from "../../../talentsStatusSlice";
import {
  CandidateGroupItem,
  ExclusivityBlockingDialogState,
  SelectedTalent,
  TalentStatusDialogState,
} from "../../../types/editOrder";
import {
  CandidateStatus,
  CandidateStatusGroupName,
  TALENTS_STATUS_COLUMNS,
  TALENTS_STATUS_GROUPS_DEFAULT_VALUES,
  TalentStatusDialogContent,
} from "../../constants";
import { TalentActionBanner } from "../TalentActionBanner";
import { WorkOfferType } from "../workAgreement/helpers";
import { WorkAgreementTypeDialog } from "../workAgreement/workAgreementTypeDialog/WorkAgreementTypeDialog";
import {
  getRecommendedCandidateTier,
  sortRecommendedActiveCandidates,
  sortRecommendedNewCandidates,
} from "./sortNewRecommendedCandidates";
import {
  sortPickedCandidates,
  sortRejectedCandidates,
} from "./sortPickedCandidates";
import TalentStatusDialogs from "./TalentStatusDialogs";
import { TalentStatusDisplayName } from "./TalentStatusDisplayName";
import TalentStatusIndicator from "./TalentStatusIndicator";
import { FormattedCandidate } from "./types";
import { matchingDetailsWarningKeys } from "./matchingDetailsWarningKeys";
import useUpdateLocalTalentStatusGroups from "./useUpdateLocalTalentStatusGroups";

type Props = {
  bundleId: string;
  isReadOnly: boolean;
  order: OrderEdit;
  jobAdId: string | null;
  searchDialogOpen?: boolean;
  onSearchDialogClose?: () => void;
};

const isCandidateGroupItem = (item: any): item is CandidateGroupItem =>
  "fullName" in item && typeof item.fullName === "string";

export const TalentsStatus = ({
  bundleId,
  isReadOnly,
  order,
  jobAdId,
  searchDialogOpen,
  onSearchDialogClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("order");
  const theme = useTheme();

  const [selectedTalents, setSelectedTalents] = useState<SelectedTalent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState<ItemGroup<CandidateGroupItem>[]>(
    TALENTS_STATUS_GROUPS_DEFAULT_VALUES,
  );
  const [dialogState, setDialogState] = useState<TalentStatusDialogState>({
    open: false,
    content: null,
  });
  const [isWorkAgreementTypeDialogOpen, setIsWorkAgreementTypeDialogOpen] =
    useState(false);
  const [exclusivityBlockingDialog, setExclusivityBlockingDialog] =
    useState<ExclusivityBlockingDialogState>({
      open: false,
      talent: null,
      exclusivities: [],
    });

  const [coverLetterModalProps, setCoverLetterModalProps] = useState({
    open: false,
    talentId: "",
    jobAdId: "",
    talentFullName: "",
  });

  const pickedCandidates = useAppSelector(selectAllPickedCandidates);
  const recommendedCandidates = useAppSelector(selectAllRecommendedCandidates);
  // jobAd applicants from talent status slice
  const jobAdApplicants = useAppSelector(selectAllTalentStatusJobAdApplicants);

  const talentCount = groups.reduce(
    (acc, group) => acc + group.items.length,
    0,
  );

  const searchState = useGroupedTableSearch({
    position: { top: "5px", left: 0 },
    placeholder: t("findFromList"),
  });

  // TODO: consider to isolate this logic in a separate hook
  // TODO: search finds tyynelä-test jussi, but not jussi tyynelä
  useEffect(() => {
    const searchValueLowerCase = searchState.searchValue.toLowerCase();

    const searchResults: CandidateGroupItem[] = [];

    // find candidates from all groups except "search"
    groups.forEach((group) => {
      if (group.name !== "search") {
        group.items.forEach((item) => {
          if (isCandidateGroupItem(item)) {
            const itemFullNameLowerCase = item.fullName.toLowerCase();

            if (itemFullNameLowerCase.includes(searchValueLowerCase)) {
              searchResults.push(item);
            }
          }
        });
      }
    });

    setGroups((prevData) => {
      // hasChanged for avoiding unnecessary state updates/infine loops
      let hasChanged = false;

      const updatedGroups = prevData.map((group) => {
        if (group.name === "search") {
          // if search term is empty, clear the items of the "search" group
          if (searchValueLowerCase === "") {
            // check if search group items are already empty
            if (group.items.length > 0) {
              hasChanged = true;
              return { ...group, items: [] };
            }
          } else if (
            // if the search term is not empty, check if the search results have changed
            group.items.length !== searchResults.length ||
            !group.items.every(
              // check if the current search results are the same as the previous ones
              (item, index) =>
                (item as CandidateGroupItem).id === searchResults[index].id,
            )
          ) {
            hasChanged = true;
            // update the changed search group items
            return { ...group, items: searchResults };
          }
        }
        return group;
      });

      // if any of the groups have been updated, return the updated groups, otherwise return the original ones
      return hasChanged ? updatedGroups : prevData;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.searchValue]);

  const updateSelectedItems = (selectedItems: SelectedTalent[]) => {
    setSelectedTalents(selectedItems);
  };

  const setGroupData = useCallback(
    // TODO: add type to data
    (
      groupName: string,
      data: any[] = [],
      status: ItemGroupLoadingState = ItemGroupLoadingState.SUCCEEDED,
    ) => {
      setGroups((prevData) =>
        prevData.map((group) =>
          group.name === groupName
            ? {
                ...group,
                items: data,
                status,
              }
            : group,
        ),
      );
    },
    [setGroups],
  );

  const openCoverLetterDialog = useCallback(
    (talentId: string, talentFullName: string) => {
      setCoverLetterModalProps({
        open: true,
        talentId,
        jobAdId: jobAdId || "",
        talentFullName,
      });
    },
    [jobAdId],
  );

  const formatPickedCandidate = useCallback(
    (pickedCandidate: PickedCandidate): FormattedCandidate => {
      const warningMessages = matchingDetailsWarningKeys(pickedCandidate).map(
        (key) => t(key),
      );
      const uiStatusValues = [
        CandidateStatus.Picked,
        CandidateStatus.InterestAsked,
        CandidateStatus.Interested,
        CandidateStatus.Introduced,
        CandidateStatus.ChosenForInterview,
        CandidateStatus.CustomerApproved,
        CandidateStatus.Offered,
        CandidateStatus.WorkAgreementAccepted,
        CandidateStatus.EezyRejected,
        CandidateStatus.Withdrew,
      ];
      const statusComponents = uiStatusValues.reduce(
        (acc, status, index, array) => {
          const currentStatusCompleted = !!pickedCandidate[status];
          const nextStatus = array[index + 1];
          const nextStatusCompleted = nextStatus
            ? !!pickedCandidate[nextStatus]
            : false;
          const isLast = status === CandidateStatus.Withdrew;
          const noStatuses = uiStatusValues.every(
            (status) => !pickedCandidate.hasOwnProperty(status),
          );
          const statusColor =
            (status === CandidateStatus.Interested &&
              pickedCandidate.interested?.success === true) ||
            (status === CandidateStatus.CustomerApproved &&
              pickedCandidate.customerApproved?.success === true) ||
            status === CandidateStatus.WorkAgreementAccepted
              ? "success.main"
              : "primary.main";

          acc[status] = (
            <TalentStatusIndicator
              key={status}
              currentStatusCompleted={currentStatusCompleted}
              nextStatusCompleted={nextStatusCompleted}
              noStatuses={noStatuses}
              success={pickedCandidate[status]?.success}
              rejectionDetailsVisible={
                status === CandidateStatus.EezyRejected ||
                pickedCandidate[status]?.success === false
              }
              rejectionReason={
                status === CandidateStatus.CustomerApproved ||
                status === CandidateStatus.EezyRejected
                  ? pickedCandidate[status]?.rejectionReason
                  : undefined
              }
              rejectionComment={
                status === CandidateStatus.CustomerApproved ||
                status === CandidateStatus.EezyRejected
                  ? pickedCandidate[status]?.rejectionComment
                  : undefined
              }
              isLast={isLast}
              color={statusColor}
              at={pickedCandidate[status]?.at}
              by={
                status !== CandidateStatus.Interested &&
                status !== CandidateStatus.WorkAgreementAccepted &&
                pickedCandidate[status]?.firstName &&
                pickedCandidate[status]?.lastName
                  ? `${pickedCandidate[status]?.firstName} ${pickedCandidate[status]?.lastName}`
                  : undefined
              }
            />
          );
          return acc;
        },
        {} as { [key: string]: JSX.Element },
      );

      // Display either work offer link or overlapping assignment link.
      // Show assignment link if work agreement has not been offered but it has been accepted
      // (this is because expert creates the assignment without talent needing to accept)
      const assignmentId = order.assignedTalents?.find(
        (talent) => talent.id === pickedCandidate.talentId,
      )?.assignmentId;
      const linkComponent = pickedCandidate.workAgreementId ? (
        <Link
          style={{
            color: theme.palette.primary.main,
          }}
          to={routes.workAgreementEdit(
            bundleId,
            pickedCandidate.workAgreementId,
          )}
          state={{
            talentId: pickedCandidate.talentId,
            assignmentId,
          }}
        >
          {t("order:showWorkOffer")}
        </Link>
      ) : pickedCandidate.workAgreementAccepted && !pickedCandidate.offered ? (
        <Link
          style={{
            color: theme.palette.primary.main,
          }}
          to={`/home/talents/${pickedCandidate.talentId}/assignment/${assignmentId}`}
          state={{
            talent: {
              fullName: getName(pickedCandidate),
              id: pickedCandidate.talentId,
            },
          }}
        >
          {t("order:showOverlappingAssignment")}
        </Link>
      ) : pickedCandidate.hasCoverLetter ? (
        <UnderlineLink
          onClick={() =>
            openCoverLetterDialog(
              pickedCandidate.talentId,
              getName(pickedCandidate),
            )
          }
        >
          {t("order:showCoverLetter")}
        </UnderlineLink>
      ) : undefined;

      return {
        id: pickedCandidate.talentId,
        fullName: getName(pickedCandidate),
        displayName: (
          <TalentStatusDisplayName
            bundleId={bundleId}
            talentId={pickedCandidate.talentId}
            fullName={getName(pickedCandidate)}
            hasCoverLetter={pickedCandidate.hasCoverLetter}
            status={pickedCandidate.status}
            statusChanged={pickedCandidate.statusChanged}
            exclusivityEntryIds={pickedCandidate.exclusivityEntryIds}
            rejectionMessageSent={pickedCandidate.rejectionMessageSent}
            warningMessages={warningMessages}
            overlappingAssignments={pickedCandidate.overlappingAssignments}
          />
        ),
        status: pickedCandidate.status,
        link: linkComponent,
        appliedAt: epochToString(
          leadingZerosDateFormat,
          pickedCandidate.applieadAt,
        ),
        exclusivityEntryIds: pickedCandidate.exclusivityEntryIds,
        overlappingAssignments: pickedCandidate.overlappingAssignments,
        ...statusComponents,
      };
    },
    [t, theme, order.assignedTalents, bundleId, openCoverLetterDialog],
  );

  const formatCandidate = useCallback(
    (candidate: Candidate): FormattedCandidate => {
      const warningMessages = matchingDetailsWarningKeys(candidate).map((key) =>
        t(key),
      );

      return {
        fullName: getName(candidate),
        displayName: (
          <TalentStatusDisplayName
            talentId={candidate.talentId}
            fullName={getName(candidate)}
            status={candidate.status}
            statusChanged={candidate.statusChanged}
            exclusivityEntryIds={candidate.exclusivityEntryIds}
            warningMessages={warningMessages}
            overlappingAssignments={candidate.overlappingAssignments}
          />
        ),
        status: candidate.status,
        id: candidate.talentId,
        appliedAt: epochToString(leadingZerosDateFormat, candidate.applieadAt),
        exclusivityEntryIds: candidate.exclusivityEntryIds,
        overlappingAssignments: candidate.overlappingAssignments,
        matchingTier: getRecommendedCandidateTier(candidate),
      };
    },
    [t],
  );

  const formatJobAdApplicant = useCallback(
    (applicant: JobAdApplicant): FormattedCandidate => {
      const linkComponent = applicant.hasCoverLetter ? (
        <UnderlineLink
          onClick={() =>
            openCoverLetterDialog(applicant.id, getName(applicant))
          }
        >
          {t("order:showCoverLetter")}
        </UnderlineLink>
      ) : (
        <></>
      );
      return {
        id: applicant.id,
        fullName: getName(applicant),
        displayName: (
          <TalentStatusDisplayName
            talentId={applicant.id}
            fullName={getName(applicant)}
            hasCoverLetter={applicant.hasCoverLetter}
            status={applicant.status}
            statusChanged={applicant?.statusChanged}
            exclusivityEntryIds={applicant.exclusivityEntryIds}
          />
        ),
        link: linkComponent,
        status: applicant.status,
        appliedAt: epochToString(leadingZerosDateFormat, applicant.appliedAt),
        exclusivityEntryIds: applicant.exclusivityEntryIds,
      };
    },
    [t, openCoverLetterDialog],
  );

  // update talent status groups, e.g. move talents from recommended to picked
  useUpdateLocalTalentStatusGroups({
    pickedCandidates,
    recommendedCandidates,
    jobAdApplicants,
    setGroupData,
    formatPickedCandidate,
    formatCandidate,
    formatJobAdApplicant,
    updateSelectedItems,
    sortPickedCandidates,
  });
  /**
   * Hack to patch picked candidates with assigned talents to show those as "Tarjous OK"
   * in the status row.
   *
   * @param pickedCandidates the original picked candidates
   * @returns picked candidates added with assigned talents
   */
  const patchPickedCandidatesWithAssignedTalents = useCallback(
    (pickedCandidates: PickedCandidate[]) => {
      let picked: PickedCandidate[] = [];
      // AssignedTalents can include cancelled talents because of replaced work agreements, so filter those out.
      // Also filter out duplicates that have been caused by replacing work agreements.
      let assignedNotCancelled: AssignedTalent[] = [];
      order.assignedTalents
        ?.filter((talent) => !talent.reasonForCancellation)
        .forEach((assignedTalent) => {
          if (!assignedNotCancelled.some((t) => t.id === assignedTalent.id)) {
            assignedNotCancelled.push(assignedTalent);
          }
        });
      if (
        pickedCandidates.length > 0 &&
        assignedNotCancelled &&
        assignedNotCancelled.length > 0
      ) {
        let updated = [];

        for (const talent of assignedNotCancelled) {
          const pci = pickedCandidates.findIndex(
            (c) => c.talentId === talent.id,
          );
          if (pci > -1) {
            const pc = pickedCandidates[pci];
            // TODO better paramters
            const pc2 = {
              ...pc,
              workAgreementAccepted: {
                userId: "",
                firstName: talent.name,
                lastName: talent.name,
                success: true,
                at: pc.workAgreementAccepted?.at || 0,
              },
              appliedAt: pc.applieadAt,
            };
            picked.push(pc2);
            updated.push(pc2.talentId);
          }
        }

        // push also the rest of the picked candidates
        if (picked.length > 0 && picked.length < pickedCandidates.length) {
          for (const pc of pickedCandidates) {
            if (!updated.includes(pc.talentId)) {
              picked.push(pc);
            }
          }
        }
      } else {
        picked = pickedCandidates;
      }
      return picked;
    },
    [order.assignedTalents],
  );

  const fetchGroupData = useCallback(
    async (bundleId: string) => {
      // set groups data on initial load
      const fetchCandidatesAction = await dispatch(
        fetchCandidates({
          bundleId,
          picked: true,
          recommended: !isReadOnly, // recommended candidates are not needed if the order is read-only
          applicants: true,
        }),
      );

      if (isFulfilled(fetchCandidatesAction)) {
        const sortedPicked = sortPickedCandidates(
          patchPickedCandidatesWithAssignedTalents(
            fetchCandidatesAction.payload.picked,
          ),
        );
        setGroupData(
          CandidateStatusGroupName.PICKED,
          sortedPicked
            .filter(
              (candidate) => !candidate.eezyRejected && !candidate.withdrew,
            )
            .map(formatPickedCandidate),
        );
        setGroupData(
          CandidateStatusGroupName.JOB_AD,
          fetchCandidatesAction.payload.applicants
            .sort((a, b) => a.appliedAt - b.appliedAt)
            .map(formatJobAdApplicant),
        );
        setGroupData(
          CandidateStatusGroupName.RECOMMENDED_NEW,
          sortRecommendedNewCandidates(
            fetchCandidatesAction.payload.recommended.filter((candidate) =>
              isLongOrderNewTalentStatus(candidate.status),
            ),
          ).map(formatCandidate),
        );
        setGroupData(
          CandidateStatusGroupName.RECOMMENDED,
          sortRecommendedActiveCandidates(
            fetchCandidatesAction.payload.recommended.filter(
              (candidate) => !isLongOrderNewTalentStatus(candidate.status),
            ),
          ).map(formatCandidate),
        );
        setGroupData(
          CandidateStatusGroupName.REJECTED,
          sortRejectedCandidates(
            sortedPicked.filter(
              (candidate) => !!candidate.eezyRejected && !candidate.withdrew,
            ),
          ).map(formatPickedCandidate),
        );
        setGroupData(
          CandidateStatusGroupName.WITHDREW,
          sortedPicked
            .filter((candidate) => !!candidate.withdrew)
            .map(formatPickedCandidate),
        );
      }
    },
    [
      setGroupData,
      dispatch,
      formatPickedCandidate,
      formatCandidate,
      isReadOnly,
      formatJobAdApplicant,
      patchPickedCandidatesWithAssignedTalents,
    ],
  );

  useEffect(() => {
    if (bundleId) fetchGroupData(bundleId).finally(() => setIsLoading(false));
  }, [bundleId, fetchGroupData]);

  const onDialogClose = () => {
    setDialogState({ open: false, content: null });
    onSearchDialogClose && onSearchDialogClose();
  };
  const onDialogOpen = (content: TalentStatusDialogContent) =>
    setDialogState({ open: true, content });

  useEffect(() => {
    if (searchDialogOpen) {
      setDialogState({
        open: true,
        content: TalentStatusDialogContent.SEARCH_OTHER_CANDIDATES,
      });
    }
  }, [searchDialogOpen]);

  return (
    <Box
      sx={{
        minWidth: "1140px",
        paddingBottom: selectedTalents.length ? "68px" : 0,
      }}
    >
      <OverlaySpinner isLoading={isLoading} />
      <Box sx={{ backgroundColor: "white", borderRadius: "8px" }}>
        <GroupedTable
          columns={TALENTS_STATUS_COLUMNS}
          groups={groups}
          itemCount={talentCount}
          translation="order"
          updateSelectedItems={updateSelectedItems}
          allowBorders
          tableHeaderBackgroundColor="#fff"
          search={searchState}
          clearSelectedItems={!selectedTalents.length}
          disableSelectAllItems
        />
        {selectedTalents.length ? (
          <TalentActionBanner
            selectedTalents={selectedTalents}
            bundleId={bundleId}
            onDialogOpen={onDialogOpen}
            openWorkAgreementTypeDialog={() =>
              setIsWorkAgreementTypeDialogOpen(true)
            }
            openExclusivityBlockingDialog={(
              talent: SelectedTalent,
              exclusivities: TalentExclusivity[],
            ) =>
              setExclusivityBlockingDialog({
                open: true,
                talent,
                exclusivities,
              })
            }
          />
        ) : null}
      </Box>
      <TalentStatusDialogs
        open={dialogState.open}
        content={dialogState.content}
        onDialogClose={onDialogClose}
        selectedTalents={selectedTalents}
        updateSelectedItems={updateSelectedItems}
        bundleId={bundleId}
        pickedCandidates={pickedCandidates}
      />
      <WorkAgreementTypeDialog
        selectedTalent={selectedTalents[0]}
        bundleId={bundleId}
        isOpen={isWorkAgreementTypeDialogOpen}
        closeDialog={() => setIsWorkAgreementTypeDialogOpen(false)}
        defaultType={WorkOfferType.New}
      />
      <WorkAgreementBlockingDialog
        open={exclusivityBlockingDialog.open}
        onClose={() =>
          setExclusivityBlockingDialog({
            open: false,
            talent: null,
            exclusivities: [],
          })
        }
        talent={exclusivityBlockingDialog.talent}
        exclusivities={exclusivityBlockingDialog.exclusivities}
      />
      <AssignmentCoverLetter
        status={coverLetterModalProps}
        closeDialog={() =>
          setCoverLetterModalProps({
            open: false,
            talentId: "",
            jobAdId: "",
            talentFullName: "",
          })
        }
      />
    </Box>
  );
};
